/** @jsxImportSource @emotion/react */

import { DATADOG_ACTIONS } from '@app/app-constants'
import { Accordion } from '@app/components/Atoms/Accordion'
import {
  DialogData,
  HazardDetail,
  HazardScenario,
} from '@app/components/Molecules/MapView/RiskMapView/AddLayersDialog'
import { HazardOptionState } from '@app/components/Molecules/MapView/RiskMapView/AddLayersDialog/HazardLayersForm/HazardBox'
import {
  layerDataByHazardId,
  layerDataByHazardIdDetail,
} from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDataFetcher'
import { MoreInformationModal } from '@app/components/Organisms/Modals/MoreInformationModal'
import { usePreferences } from '@app/hooks/usePreferences'
import { hazardOptionStateAtom } from '@app/risk-migration/atoms/riskMap.atoms'
import { useRiskLayersState } from '@app/risk-migration/hooks/useRiskLayersState'
import type { MapLayer } from '@app/types/layerTypes'
import { logDataDogAction } from '@app/utils/logger'
import { Box, Typography, useTheme } from '@mui/material'
import { useAtom } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { HazardOptionStateByHazard } from '../HazardControls'
import { HazardControlFormElement } from './HazardControlFormElement_Legacy'
import { HazardControlFormHeader_Legacy } from './HazardControlFormHeader'
import {
  hazardControlRightColumn,
  hazardControlsColumn,
  hazardControlsRow,
  hazardDetailFormContainer,
  hiddenOptionsNote,
} from './HazardControls.styles'
import { HazardControlShownScenario } from './HazardControlShownScenario'
export interface GenericKeyValues<T> {
  [key: string]: T
}

export interface FormElementType {
  key: string
  title: string
  type: string
  priority: number
}

interface HazardOptionState_Legacy {
  [key: string]: {
    value: string | number | number[]
  }
}

interface HazardOptionStateByHazard_Legacy {
  [key: string]: HazardOptionState_Legacy
}

const convertHazardOptionStateToLegacy = (hazardOptionState: HazardOptionState) => {
  const legacyState: HazardOptionState_Legacy = {}
  for (const { key, value } of hazardOptionState) {
    legacyState[key] = { value }
  }
  return legacyState
}
const convertHazardOptionStateFromLegacy = (hazardOptionStateLegacy: HazardOptionState_Legacy) => {
  const hazardOptionState: HazardOptionState = []
  for (const [key, { value }] of Object.entries(hazardOptionStateLegacy)) {
    hazardOptionState.push({ key, value })
  }
  return hazardOptionState
}
const convertHazardOptionStateByHazardToLegacy = (
  hazardOptionStateByHazard: HazardOptionStateByHazard,
) => {
  const legacyState: HazardOptionStateByHazard_Legacy = {}
  for (const [hazardId, options] of Object.entries(hazardOptionStateByHazard)) {
    legacyState[hazardId] = convertHazardOptionStateToLegacy(options)
  }
  return legacyState
}
const convertHazardOptionStateByHazardFromLegacy = (
  hazardOptionStateByHazardLegacy: HazardOptionStateByHazard_Legacy,
) => {
  const hazardOptionStateByHazard: HazardOptionStateByHazard = {}
  for (const [hazardId, options] of Object.entries(hazardOptionStateByHazardLegacy)) {
    hazardOptionStateByHazard[hazardId] = convertHazardOptionStateFromLegacy(options)
  }
  return hazardOptionStateByHazard
}

export type CombinedHazardDetail = HazardDetail & layerDataByHazardIdDetail

/*
 * Given a hazardDetail, return the current scenario using layers
 */
export const getScenarioByHazardDetail = (
  layers: MapLayer[],
  hazardDetail: CombinedHazardDetail,
) => {
  if (hazardDetail.hazard_id)
    return layers.find(
      (layer) => layer.hazard_id === (hazardDetail as CombinedHazardDetail).hazard_id,
    )
  return layers.find((layer) => layer.hazard_title === hazardDetail.title)
}

export const HazardControls_Legacy = ({
  layersData,
  scenarioByHazardId,
}: {
  layersData: DialogData | null
  scenarioByHazardId: layerDataByHazardId | undefined
  hazardIds: string[]
}) => {
  const { term_preference, use_hazard_name } = usePreferences()
  const theme = useTheme()

  const { layers, setLayers } = useRiskLayersState()
  const [newHazardOptionState, newSetHazardOptionState] = useAtom(hazardOptionStateAtom)
  const hazardOptionState = useMemo(() => {
    return convertHazardOptionStateByHazardToLegacy(newHazardOptionState)
  }, [newHazardOptionState])
  const setHazardOptionState = (
    state: (prev: HazardOptionStateByHazard_Legacy) => HazardOptionStateByHazard_Legacy,
  ) => {
    newSetHazardOptionState((newPrev) => {
      const prev = convertHazardOptionStateByHazardToLegacy(newPrev)
      return convertHazardOptionStateByHazardFromLegacy(state(prev))
    })
  }
  const [lastUpdatedHazard, setLastUpdatedHazard] = useState(null as HazardDetail | null)
  const [informationDetailsModal, setInformationDetailsModal] = useState({
    isOpen: false,
    helpFileName: '',
  })
  const [keysWithNoValues, setKeysWithNoValues] = useState<{
    [key: string]: { [key: string]: boolean }
  }>({})
  const hazardMapLayers = useMemo(() => {
    return layers.filter((layer: MapLayer) => layer.layerType === 'hazard')
  }, [layers])

  const filteredLayersDataMemoKey = useMemo(() => {
    // used to control when to recalc filteredLayersData
    const hazardString = hazardMapLayers
      .map((layer) => layer.assetTag ?? layer.type ?? layer.id)
      .toSorted()
      .join(',')
    const scenarioString = scenarioByHazardId
      ? Object.keys(scenarioByHazardId)
          .map((hId) => {
            const scenarioIds = scenarioByHazardId[hId].scenarios
              .map((scenario) => scenario.hazard_id ?? scenario.assetTag ?? scenario.type)
              .toSorted()
            return `${hId}:${scenarioIds.join('-')}`
          })
          .toSorted()
          .join(',')
      : ''
    return `${hazardString}|${scenarioString}`
  }, [hazardMapLayers, scenarioByHazardId])

  /**
   * Get the dialog layer data that matches the hazardMapLayers
   * VERY side effect heavy
   * Different returns depending on if hazard_id's are present
   */
  const filteredLayersData = useMemo(() => {
    if (!layersData) return []
    const hasHazardIds = layersData?.hazardDialogData.hazardDetails.some(
      (HazardDetail: HazardDetail) =>
        HazardDetail.scenarios.some((scenario: HazardScenario) => scenario.hazard_id),
    )

    if (hasHazardIds && scenarioByHazardId) {
      const hazardIdRes = Object.keys(scenarioByHazardId).reduce((acc, hId) => {
        const foundCurrentScenario = scenarioByHazardId[hId].scenarios.find((scenario) => {
          return hazardMapLayers.some((layer: MapLayer) => {
            return layer.assetTag === scenario.assetTag
          })
        })

        if (foundCurrentScenario) {
          acc.push({
            ...scenarioByHazardId[hId],
            title: `${scenarioByHazardId[hId].hazard_name}`,
            scenarios: scenarioByHazardId[hId].scenarios.filter(
              (scenario) => scenario.hazard_id === foundCurrentScenario?.hazard_id,
            ),
          })
        }

        return acc
      }, [] as CombinedHazardDetail[])
      return hazardIdRes
    }
    const res = layersData.hazardDialogData.hazardDetails.filter((hazardDetail: HazardDetail) => {
      // filter based on title
      return hazardMapLayers.some((layer: MapLayer) => {
        const comparison = (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title
        return hasHazardIds
          ? (layer?.hazard_id ?? '' === comparison)
          : layer.hazard_title === comparison
      })
    })
    return res
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredLayersDataMemoKey])

  /**
   * Used for reactivity as a primitive
   */
  const hazardTitleString = useMemo(() => {
    const hazardString = hazardMapLayers
      .map((hazardDetail) => {
        return hazardDetail?.assetTag ?? hazardDetail.display_name
      })
      .join(', ')
    return hazardString
  }, [hazardMapLayers])

  const sortFormByPriority = (form: HazardDetail['form']) => {
    return [...form].sort((a, b) => a.priority - b.priority)
  }

  /**
   * Set all values to '' if not already set
   */
  const getInitialHazardOptionState = () => {
    const initialHazardOptionState: HazardOptionStateByHazard_Legacy = { ...hazardOptionState }
    filteredLayersData?.forEach((hazardDetail) => {
      const hazardKey = (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title
      if (!initialHazardOptionState[hazardKey]) {
        initialHazardOptionState[hazardKey] = {}
      }
      for (const formElement of hazardDetail.form) {
        if (hazardKey in initialHazardOptionState) {
          const currentFormValue = hazardOptionState?.[hazardKey]?.[formElement.key]?.value

          initialHazardOptionState[hazardKey][formElement.key] = {
            value: currentFormValue ?? '',
          }
        }
      }
    })
    return initialHazardOptionState
  }

  /**
   * find the scenario based on the hazardOptionState
   */
  const findScenario = (
    scenarios: HazardScenario[],
    hazardKey: string,
  ): HazardScenario | undefined => {
    if (!hazardOptionState[hazardKey]) return
    const res = scenarios.find((scenario: HazardScenario) => {
      const hazardOptionKeys = Object.keys(hazardOptionState[hazardKey])
      const isAvailable = hazardOptionKeys.every((optionKey) => {
        const optionValue = hazardOptionState[hazardKey][optionKey]?.value
        if (
          optionValue === '' ||
          optionValue === null ||
          optionValue === undefined ||
          optionValue === 'Not Specified'
        )
          return true

        return Object.keys(scenario.parameters).some((param) => {
          if (param === optionKey) {
            return scenario.parameters[param].toString() === optionValue.toString()
          }
        })
      })
      return isAvailable
    })

    return res
  }

  const hasMultipleAvailableValues = (hazardDetail: HazardDetail) => {
    const parameterKeys = Object.keys(hazardDetail.scenarios[0]?.parameters || {})
    return parameterKeys.some((key) => {
      const uniqueValues = [
        ...new Set(hazardDetail.scenarios.map((scenario) => scenario.parameters[key])),
      ]
      return uniqueValues.length > 1
    })
  }

  const updateHazardOptionState = ({
    key,
    value,
    hazardDetail,
  }: {
    key: string
    value: string | number
    hazardDetail: CombinedHazardDetail | HazardDetail
  }) => {
    setHazardOptionState((prevState: HazardOptionStateByHazard_Legacy) => {
      const hazardKey = (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title
      return {
        ...prevState,
        [hazardKey]: {
          ...prevState[hazardKey],
          [key]: {
            value,
          },
        },
      }
    })
    setLastUpdatedHazard(hazardDetail)

    logDataDogAction(DATADOG_ACTIONS.MODIFY_HAZARD_SCENARIO, {
      id: (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title,
    })
  }
  /**
   * Switch between the present value and the value from the scenario whenever a switch is clicked downline
   */
  const handleSwitch = (key: string, isOn: boolean, hazardDetail: HazardDetail) => {
    if (isOn) updateHazardOptionState({ key, value: '', hazardDetail })
    else {
      const scenario = getScenarioByHazardDetail(layers, hazardDetail as CombinedHazardDetail)
      if (!scenario?.parameters) return
      updateHazardOptionState({ key, value: scenario.parameters[key], hazardDetail })
    }
  }

  const handleNoteKeys = (hazardDetail: HazardDetail, key: string, value: boolean) => {
    setKeysWithNoValues((prevState) => {
      const hazardKey = (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title
      return {
        ...prevState,
        [hazardKey]: {
          [key]: value,
        },
      }
    })
  }

  /**
   * final function call for updating layers based on the hazard option state and findScenario
   * @TODO don't call if data is same
   */
  const updateLayersFromHazardOptionState = ({
    hazardDetail,
  }: {
    hazardDetail: CombinedHazardDetail | HazardDetail
  }) => {
    const allScenariosForHazard = scenarioByHazardId
      ? scenarioByHazardId[(hazardDetail as CombinedHazardDetail).hazard_id]?.scenarios
      : layersData?.hazardDialogData?.hazardDetails.find(
          (hazard) => hazard.title === hazardDetail.title,
        )?.scenarios
    let scenarios = hazardDetail.scenarios
    if (allScenariosForHazard) scenarios = allScenariosForHazard
    const newScenario = findScenario(
      scenarios,
      (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title,
    )
    if (!newScenario) return
    let scenarioTitle = ''

    // remove existing layer
    let newLayers = layers.filter((layer) => layer.hazard_title !== hazardDetail.title)
    // works because there is only one hazard_id per hazardDetail
    if (newScenario.hazard_id) {
      layersData?.hazardDialogData?.hazardDetails.find((hazard) => {
        return hazard.scenarios.some((scenario) => {
          if (scenario.hazard_id === newScenario.hazard_id) {
            scenarioTitle = hazard.title
            return true
          }
          return false
        })
      })
      newLayers = layers.filter((layer) => layer.hazard_id !== newScenario.hazard_id)
      // add new layer
      newLayers.push({
        ...newScenario,
        hazard_title: scenarioTitle,
        layerType: 'hazard',
        visible: true,
      })
      const layersSorted = layers
        .map((layer) => layer.assetTag ?? layer.type ?? layer.id)
        .toSorted()
      const newLayersSorted = newLayers
        .map((layer) => layer.assetTag ?? layer.type ?? layer.id)
        .toSorted()
      if (layersSorted.join() === newLayersSorted.join()) return
      setLayers(newLayers)
      return
    }

    // add new layer
    newLayers.push({
      ...newScenario,
      hazard_title: hazardDetail.title,
      layerType: 'hazard',
      visible: true,
    })
    const layersSorted = layers.map((layer) => layer.assetTag ?? layer.type ?? layer.id).toSorted()
    const newLayersSorted = newLayers
      .map((layer) => layer.assetTag ?? layer.type ?? layer.id)
      .toSorted()
    if (layersSorted.join() === newLayersSorted.join()) return
    setLayers(newLayers)
  }

  /**
   * If changes are detected, it calls the updateLayersFromHazardOptionState function with the last updated hazard as the parameter.
   * This ensures that the layers are updated whenever there is a change in the hazard option state.
   */
  const hazardOptionStateKey = useMemo(() => JSON.stringify(hazardOptionState), [hazardOptionState])
  useEffect(() => {
    if (lastUpdatedHazard === null) return
    updateLayersFromHazardOptionState({ hazardDetail: lastUpdatedHazard })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdatedHazard, hazardOptionStateKey])

  // use a string cause it won't recalc on rerender unless it actually changed cause it's a primitive
  // used for initial setup as well as on hazard changes
  useEffect(() => {
    const initialHazardOptionState = getInitialHazardOptionState()
    setHazardOptionState((prev) => {
      return {
        ...prev,
        ...initialHazardOptionState,
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hazardTitleString])

  if (!filteredLayersData) return null
  if (!Object.keys(hazardOptionState).length) return null

  return (
    <>
      <Typography>
        Adjust the slider and button controls below to view different{' '}
        {term_preference.hazard ? 'hazard' : 'risk'} scenarios.
      </Typography>
      {filteredLayersData.map((hazardDetail: CombinedHazardDetail | HazardDetail) => {
        if (!hazardDetail.form.length) return null
        if (!hasMultipleAvailableValues(hazardDetail)) return null
        const mapLayer = getScenarioByHazardDetail(layers, hazardDetail as CombinedHazardDetail)
        return (
          <Accordion
            variant="outline"
            level="h3"
            key={
              use_hazard_name.flag
                ? (mapLayer?.hazard_name ?? mapLayer?.display_name)
                : mapLayer?.display_name
            }
            header={
              <HazardControlFormHeader_Legacy
                hazardDetails={hazardDetail as CombinedHazardDetail}
              />
            }
            defaultExpandedOnce
            accordionStateKey={(hazardDetail as CombinedHazardDetail).hazard_id}
            body={
              <Box css={hazardControlsColumn}>
                <Box css={hazardControlsRow}>
                  <Box css={hazardDetailFormContainer({ theme })}>
                    <Typography
                      onClick={() => {
                        setHazardOptionState((prevState) => {
                          const newState = { ...prevState }
                          const hazardKey =
                            (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title
                          Object.keys(newState[hazardKey]).forEach((key) => {
                            newState[hazardKey][key].value = ''
                          })
                          return newState
                        })
                        updateLayersFromHazardOptionState({ hazardDetail })
                      }}
                      className="reset-filters"
                    >
                      Reset Controls
                    </Typography>
                    {sortFormByPriority(hazardDetail.form).map((formElement: FormElementType) => {
                      if (
                        !hazardOptionState[
                          (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title
                        ]
                      )
                        return null
                      return (
                        <HazardControlFormElement
                          key={formElement.key}
                          hazardDetail={hazardDetail}
                          formElement={formElement}
                          hazardOptionState={
                            hazardOptionState[
                              (hazardDetail as CombinedHazardDetail)?.hazard_id ??
                                hazardDetail.title
                            ]
                          }
                          handleParameterChange={(key, value) =>
                            updateHazardOptionState({ key, value, hazardDetail })
                          }
                          handleSwitch={(key: string, isOn: boolean) =>
                            handleSwitch(key, isOn, hazardDetail)
                          }
                          handleNoteKeys={handleNoteKeys}
                        />
                      )
                    })}
                    <Box
                      css={hiddenOptionsNote({
                        theme,
                        shouldHideNote: Object.keys(
                          keysWithNoValues[
                            (hazardDetail as CombinedHazardDetail)?.hazard_id ?? ''
                          ] ?? {},
                        ).some((innerKey) => {
                          return keysWithNoValues[
                            (hazardDetail as CombinedHazardDetail)?.hazard_id ?? ''
                          ][innerKey]
                        }),
                      })}
                    >
                      <Typography variant="h6">Note</Typography>
                      <Typography>
                        Control options will be hidden if a scenario is not possible. To show all
                        options reset controls.
                      </Typography>
                    </Box>
                  </Box>
                  <Box css={hazardControlRightColumn}>
                    <HazardControlShownScenario
                      hazardDetail={hazardDetail as CombinedHazardDetail}
                      scenario={findScenario(
                        hazardDetail.scenarios,
                        (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title,
                      )}
                      setInformationDetailsModal={setInformationDetailsModal}
                      hazardOptionState={
                        hazardOptionState[
                          (hazardDetail as CombinedHazardDetail)?.hazard_id ?? hazardDetail.title
                        ]
                      }
                    />
                  </Box>
                </Box>
              </Box>
            }
          ></Accordion>
        )
      })}
      {informationDetailsModal.isOpen &&
        createPortal(
          <MoreInformationModal
            baseURL={''}
            helpFileName={informationDetailsModal.helpFileName}
            isOpen={informationDetailsModal.isOpen}
            handleClose={() =>
              setInformationDetailsModal({
                isOpen: false,
                helpFileName: '',
              })
            }
          />,
          document.body,
        )}
    </>
  )
}
